import styled, { css } from 'styled-components';
import { rem } from 'polished';
import variables from '@styles/variables';
import Grid from '@atoms/Grid/Grid';
import Skeleton from '@atoms/Skeleton/Skeleton';
import Config from '@config';

export const SkeletonProductWrapper = styled(Grid)<{
  $offlineProduct?: boolean;
  $type?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
  $rows: number;
}>`
  margin-bottom: ${rem(16)};
  height: ${rem(404)};
  overflow: hidden;

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    height: ${rem(430)};
  }

  ${(props) =>
    props.$offlineProduct &&
    props.$rows > 1 &&
    css`
      height: ${rem(736)};
    `}

  ${(props) =>
    (props.$type === 'promotionBeam' ||
      props.$type === 'categoryBeam' ||
      (!props.$offlineProduct && props.$rows > 1)) &&
    css`
      height: ${rem(824)};
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        height: ${rem(876)};
      }
    `}
`;

export const SkeletonWrapper = styled.div<{
  $type?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  margin-bottom: ${rem(52)};

  ${(props) =>
    props.$type === 'pageBeam' &&
    css`
      margin-top: ${rem(30)};
    `}

  ${(props) =>
    props.$type === 'promotionBeam' &&
    css`
      margin-top: ${rem(20)};
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        margin-top: ${rem(32)};
      }
    `}

  ${(props) =>
    props.$type === 'categoryBeam' &&
    css`
      margin-top: ${rem(13)};
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        margin-top: ${rem(20)};
      }
    `}
`;

export const ProductWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - ${rem(14)});
  margin: ${rem(7)} ${rem(12)} ${rem(7)} ${rem(12)};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

export const ProductInformation = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(3)};
`;

export const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(5)};
  margin-left: auto;
  margin-right: auto;
  align-items: center;
`;

export const ProductContainer = styled.div<{ $offlineProduct?: boolean }>`
  border: ${rem(1)} solid ${variables.colors.lightGray};
  border-radius: ${rem(8)};
  height: ${rem(404)};

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    height: ${rem(430)};
  }

  ${(props) =>
    props.$offlineProduct &&
    css`
      height: ${rem(360)};
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        height: ${rem(360)};
      }
    `}
`;

export const SkeletonPageBeamWrapper = styled.div<{
  $variant?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(16)};
  gap: ${rem(20)};

  ${(props) =>
    props.$variant === 'categoryBeam' &&
    css`
      gap: ${rem(16)};
      margin-bottom: ${rem(12)};
    `}

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    margin-bottom: ${rem(12)};
    gap: ${rem(32)};

    ${(props) =>
      props.$variant === 'categoryBeam' &&
      css`
        margin-bottom: ${rem(28)};
      `}
  }
`;

export const SkeletonPromotionPageBeamWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(16)};
  gap: ${rem(16)};

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    margin-bottom: ${rem(28)};
  }
`;

export const SkeletonPageBeamFilters = styled.div<{
  $type?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${Config.MEDIA.IS_MOBILE} {
    gap: ${rem(12)};
  }
`;

export const SkeletonHeader = styled(Skeleton)`
  margin-bottom: ${rem(15)};
  margin-top: 0;
`;

export const SkeletonBreadCrumbs = styled(Skeleton)`
  margin-bottom: ${rem(13)};
  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    margin-bottom: ${rem(26)};
  }
`;

export const SkeletonResults = styled(Skeleton)`
  margin-left: auto;
  margin-right: auto;

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    display: none;
  }
`;

export const SkeletonFilter = styled(Skeleton)<{
  $variant?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  ${(props) =>
    (props.$variant === 'pageBeam' || props.$variant === 'categoryBeam') &&
    css`
      width: 100%;
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        width: ${rem(113)};
      }
    `}
`;

export const SkeletonSorting = styled(Skeleton)<{
  $variant?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  ${(props) =>
    (props.$variant === 'pageBeam' || props.$variant === 'categoryBeam') &&
    css`
      width: 100%;
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        width: ${rem(172)};
      }
    `}
`;

export const SkeletonPageHeader = styled(Skeleton)<{
  $variant?: 'buyAll' | 'pageBeam' | 'productBeam' | 'promotionBeam' | 'categoryBeam';
}>`
  ${(props) =>
    (props.$variant === 'pageBeam' || props.$variant === 'categoryBeam') &&
    css`
      width: 100%;
      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        width: ${rem(300)} !important;
      }
    `}

  ${(props) =>
    props.$variant === 'categoryBeam' &&
    css`
      ${Config.MEDIA.IS_MOBILE} {
        display: none;
      }
    `}
`;

export const SkeletonResultsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${rem(16)};

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    justify-content: flex-end;
    margin-bottom: ${rem(12)};
  }
`;

export const SkeletonResultsMobile = styled(Skeleton)`
  display: none;

  ${Config.MEDIA.IS_MOBILE} {
    display: block;
  }
`;

export const SkeletonFiltersMobile = styled(Skeleton)`
  display: none;
  margin-bottom: ${rem(20)};

  ${Config.MEDIA.IS_MOBILE} {
    display: block;
  }
`;
